import type { GeneratedDefinition, GeneratedStatus } from "@keydials/api-client";
import type { DialModel } from "@keydials/dial-renderer";
import { addStatus, BUILDER_PRESETS, currentPreset, definitionToDialModel, type BuilderPresetId } from "@keydials/dial-workspace";
import { builderCopy as copy } from "./copy";

// ---------------------------------------------------------------------------
// The Colours stage (KEY-560): three presets and "Customise". The edits themselves are the shared palette edits; this decides
// which preset card shows as chosen, what the wheel beside the cards draws, and the colour a new state starts with.
// ---------------------------------------------------------------------------

export interface PresetCard {
  id: BuilderPresetId;
  title: string;
  recommended: boolean;
  states: Array<{ key: string; name: string; colour: string }>;
  selected: boolean;
}

export interface ColoursView {
  cards: PresetCard[];
  /** True once the states match no preset: no card shows as chosen, Customise opens by itself, and picking a preset asks first. */
  customised: boolean;
  itemised: boolean;
}

export function coloursView(model: GeneratedDefinition): ColoursView {
  const current = currentPreset(model);
  return {
    cards: BUILDER_PRESETS.map((preset, index) => ({
      id: preset.id,
      title: copy.colours.presets[preset.id],
      recommended: index === 0,
      states: preset.statuses.map(({ key, name, colour }) => ({ key, name, colour })),
      selected: current === preset.id,
    })),
    customised: current === "custom",
    itemised: model.kind === "Itemised",
  };
}

/** The name the preview gives the dial's own states, so a checklist dial's action points are drawn in them. */
const OWN_COLOURS = "builder-colours-preview";

/**
 * What the wheel on the Colours stage draws: the dial as it is, in its current colours. A checklist dial's action points show
 * none, some and all done in these colours, not the fixed traffic light. A dial with no action points yet shows a wedge per state.
 */
export function coloursPreview(model: GeneratedDefinition): DialModel {
  const statuses = model.statusSet.statuses;
  const hasSegments = model.sections.some((s) => s.segments.length > 0);

  if (!hasSegments) {
    return definitionToDialModel({
      statusSet: { statuses },
      kind: "Simple",
      sections: [{ key: "preview", label: "", segments: statuses.map((s, i) => ({ key: `state-${i}`, label: "", name: s.name, items: [] })) }],
    });
  }

  if (model.kind !== "Itemised") return definitionToDialModel({ statusSet: { statuses }, kind: "Simple", sections: model.sections });

  return definitionToDialModel(
    {
      statusSet: { statuses },
      kind: "Itemised",
      sections: model.sections.map((s) => ({ ...s, segments: s.segments.map((g) => ({ ...g, palette: OWN_COLOURS })) })),
    },
    [{ name: OWN_COLOURS, statuses }],
  );
}

const NEW_STATE_COLOURS = ["#2563eb", "#7c3aed", "#0d9488", "#db2777", "#ca8a04", "#64748b"];

/** A colour for a new state: the first of a few the dial isn't already using. */
export function nextStateColour(statuses: readonly GeneratedStatus[]): string {
  const used = new Set(statuses.map((s) => s.colour.toLowerCase()));
  return NEW_STATE_COLOURS.find((colour) => !used.has(colour)) ?? NEW_STATE_COLOURS[statuses.length % NEW_STATE_COLOURS.length];
}

/** Adds a state once it has a name. A blank name adds nothing, so a nameless state is never saved. */
export function addNamedState(model: GeneratedDefinition, name: string): { model: GeneratedDefinition; key: string } | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  return addStatus(model, trimmed, nextStateColour(model.statusSet.statuses));
}

/** A stored colour as a colour picker can show it (`#rrggbb`). A short hex is widened; anything else shows as black. */
export function colourInputValue(colour: string): string {
  const value = colour.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(value)) return value;
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(value);
  return short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : "#000000";
}
